import React, { useEffect, useState } from 'react';
import type { BoardModerationItemDto } from '@living-library/contracts';
import { boardError, json, type BoardRequest } from './board-api';
import { button, c, card, input } from './board-ui';

export function BoardModeration({ request }: { request: BoardRequest }) {
  const [items, setItems] = useState<BoardModerationItemDto[]>([]),
    [kind, setKind] = useState<BoardModerationItemDto['kind']>('review'),
    [query, setQuery] = useState('');
  const [hiddenOnly, setHiddenOnly] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  async function load(hidden: boolean) {
    setBusy(true);
    setError('');
    setHiddenOnly(hidden);
    try {
      setItems(
        await request(
          hidden
            ? '/moderation'
            : `/moderation?hidden=false&kind=${kind}&query=${encodeURIComponent(query)}`,
        ),
      );
    } catch (error) {
      setError(boardError(error));
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void load(true);
  }, [request]);
  async function toggle(item: BoardModerationItemDto) {
    setBusy(true);
    setError('');
    try {
      await request(`/moderation/${item.kind}/${item.id}`, json('PUT', { hidden: !item.hiddenAt }));
      await load(hiddenOnly);
    } catch (error) {
      setError(boardError(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-3xl">{c.moderation}</h2>
        <button className={button} disabled={busy} onClick={() => void load(true)}>
          {c.hidden}
        </button>
      </div>
      <form
        className={`${card} mb-5 flex flex-wrap items-end gap-3`}
        onSubmit={(event) => {
          event.preventDefault();
          void load(false);
        }}
      >
        <label>
          {c.kind}
          <select
            className={input}
            value={kind}
            onChange={(event) => setKind(event.target.value as typeof kind)}
          >
            {(['review', 'line', 'team_book'] as const).map((value) => (
              <option key={value} value={value}>
                {c[value]}
              </option>
            ))}
          </select>
        </label>
        <label className="min-w-40 flex-1">
          {c.target}
          <input
            className={input}
            value={query}
            maxLength={200}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <button className={button} disabled={busy || !query.trim()}>
          {c.search}
        </button>
      </form>
      {error && (
        <p role="alert" className="mb-4 text-warning">
          {error}
        </p>
      )}
      {busy && (
        <p className="admin-empty" role="status">
          {c.loading}
        </p>
      )}
      {!busy && !error && !items.length && <p className="admin-empty">{c.empty}</p>}
      <div className="grid gap-3">
        {items.map((item) => (
          <article
            key={`${item.kind}:${item.id}`}
            className={`${card} flex flex-wrap items-center gap-4`}
          >
            <div className="min-w-0 flex-1">
              <span className="text-xs uppercase tracking-wider text-brass-pale">
                {c[item.kind]}
              </span>
              <p className="my-2 whitespace-pre-wrap">{item.label}</p>
              <p className="break-all text-xs text-ink-muted">{item.id}</p>
              {item.hiddenAt && (
                <p className="mt-2 text-xs text-ink-soft">
                  {c.hiddenAt}: {new Date(item.hiddenAt).toLocaleString()} · {c.hiddenBy}:{' '}
                  {item.hiddenBy ?? '—'}
                </p>
              )}
            </div>
            <button className={button} disabled={busy} onClick={() => void toggle(item)}>
              {item.hiddenAt ? c.unhide : c.hide}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}
